import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { wrapLabel } from "../lib/chart_colors.js"

// Renders a distribution chart (doughnut, pie, bar, or column) from label/data arrays.
// Used for non-trackable mission task results in the modal preview.
//
// Example:
//   <canvas data-controller="distribution-chart"
//           data-distribution-chart-labels-value='["Yes","No"]'
//           data-distribution-chart-data-value='[84, 16]'
//           data-distribution-chart-chart-kind-value="doughnut"
//           data-distribution-chart-primary-color-value="#003d68">
//   </canvas>

export default class extends Controller {
  static values = {
    labels: Array,
    data: Array,
    chartKind: { type: String, default: "doughnut" },
    primaryColor: { type: String, default: "#003d68" },
    labelStyle: { type: String, default: "percentage" },
    responsesCount: { type: Number, default: 0 },
    highlightValues: { type: String, default: "" },
    showLegend: { type: Boolean, default: false },
    showCentre: { type: Boolean, default: false },
    centreValue: { type: String, default: "" },
    centreLabel: { type: String, default: "" },
    customColors: { type: Array, default: [] },
    target: { type: Number, default: 0 },
    cycleTargets: { type: Object, default: {} }
  }

  connect() {
    // If canvas is in a hidden container, defer rendering until visible
    if (this.element.offsetParent === null) {
      this._observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this._observer.disconnect()
          this._observer = null
          this.renderChart()
        }
      })
      this._observer.observe(this.element)
    } else {
      this.renderChart()
    }
  }

  disconnect() {
    if (this._observer) { this._observer.disconnect(); this._observer = null }
    if (this._chart) { this._chart.destroy(); this._chart = null }
  }

  renderChart() {
    const ctx = this.element.getContext('2d')
    const kind = this.chartKindValue
    const labels = this.labelsValue
    const data = this.dataValue
    const colors = this.buildColors(data.length)
    let effectiveColors
    if (this.customColorsValue.length === data.length) {
      // Full override: one colour per segment
      effectiveColors = this.customColorsValue
    } else if (this.customColorsValue.length === 1) {
      // Single override: use custom colour for highlighted segments, palette for the rest
      const overrideColor = this.customColorsValue[0]
      effectiveColors = labels.map((label, i) =>
        this.highlightList.includes(String(label)) ? overrideColor : colors[i]
      )
    } else {
      effectiveColors = colors
    }
    const maxValue = Math.max(...data)
    const responsesCount = this.responsesCountValue

    if (kind === 'bar' || kind === 'column') {
      const isHorizontal = kind === 'bar'
      const labelStyle = this.labelStyleValue
      const total = data.reduce((a, b) => a + b, 0)
      new Chart(ctx, {
        type: 'bar',
        plugins: [ChartDataLabels],
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: this.buildHighlightBarColors(labels, effectiveColors),
            borderWidth: 0
          }]
        },
        options: {
          indexAxis: isHorizontal ? 'y' : 'x',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: this.showLegendValue },
            tooltip: { enabled: true },
            datalabels: {
              display: labelStyle !== 'none',
              color: (context) => {
                const value = context.dataset.data[context.dataIndex]
                return (value / maxValue) > 0.15 ? '#ffffff' : '#374151'
              },
              anchor: 'end',
              align: (context) => {
                const value = context.dataset.data[context.dataIndex]
                return (value / maxValue) > 0.15 ? 'start' : 'end'
              },
              font: { weight: 'bold', size: 12 },
              formatter: (value) => {
                if (value <= 0) return ''
                const pctOfTotal = Math.round((value / total) * 100)
                const pctOfResponses = responsesCount > 0 ? Math.round((value / responsesCount) * 100) : pctOfTotal
                if (labelStyle === 'count') return value
                if (labelStyle === 'percentage') return `${pctOfTotal}%`
                if (labelStyle === 'percentage_of_responses') return `${pctOfResponses}%`
                if (labelStyle === 'both') return `${value} (${pctOfTotal}%)`
                return value
              }
            }
          },
          scales: {
            x: { beginAtZero: true, ticks: { precision: 0 }, grid: { display: false } },
            // Card #4273 round 2: horizontal mode (indexAxis: 'y') puts the
            // category labels on this axis -- wrap/truncate those the same
            // way bar_chart_controller.js and chart_preview_controller.js do,
            // or a long label clips at the canvas edge. Vertical/column mode
            // uses this axis for numeric values, so the callback is left off.
            y: {
              beginAtZero: true,
              grid: { display: false },
              ticks: isHorizontal
                ? { precision: 0, callback: (value, index) => wrapLabel(labels[index]) }
                : { precision: 0 }
            }
          },
          animation: { duration: 600 }
        }
      })
    } else if (kind === 'text') {
      const total = data.reduce((a, b) => a + b, 0)
      this.element.parentElement.innerHTML = `
        <div class="flex items-center justify-center py-8">
          <div class="text-5xl font-bold" style="color: ${this.primaryColorValue};">${total}</div>
        </div>
      `
    } else if (kind === 'stat') {
      // Stat Card: total + min/max/avg with bullet range
      const total = data.reduce((a, b) => a + b, 0)
      const numericKeys = labels.map(Number).filter(n => !isNaN(n))
      const hasNumericKeys = numericKeys.length > 0

      let min, max, avg
      if (hasNumericKeys) {
        // Weighted stats from numeric answer values
        const pairs = labels.map((l, i) => ({ val: parseFloat(l), count: data[i] })).filter(p => !isNaN(p.val))
        const allValues = pairs.flatMap(p => Array(p.count).fill(p.val))
        min = Math.min(...allValues)
        max = Math.max(...allValues)
        avg = (allValues.reduce((a, b) => a + b, 0) / allValues.length).toFixed(1)
      } else {
        // For non-numeric: min/max/avg of the counts themselves
        min = Math.min(...data)
        max = Math.max(...data)
        avg = (total / data.length).toFixed(1)
      }

      const primary = this.primaryColorValue
      const bulletPct = max > 0 ? ((avg - min) / (max - min)) * 100 : 50

      const canary = '#fcaf17'
      this.element.parentElement.innerHTML = `
        <div class="flex flex-col items-center justify-center h-full gap-6 py-4">
          <div class="text-center">
            <div class="text-6xl font-bold leading-none" style="color: ${primary};">${total.toLocaleString()}</div>
            <div class="text-sm text-gray-400 mt-2 uppercase tracking-wide">Total ${hasNumericKeys ? '' : 'Responses'}</div>
          </div>
          <div class="w-full max-w-sm">
            <div class="relative h-6 bg-gray-100 rounded-full overflow-hidden">
              <div class="absolute inset-y-0 left-0 rounded-full" style="width: ${max > 0 ? (avg / max * 100) : 50}%; background: ${primary};"></div>
              <div class="absolute top-0 bottom-0 w-1 rounded" style="left: ${bulletPct}%; background: ${canary};"></div>
            </div>
          </div>
          <div class="grid grid-cols-3 gap-4 w-full max-w-sm">
            <div class="bg-gray-50 rounded-lg p-3 text-center">
              <div class="text-lg font-bold text-gray-900">${min}</div>
              <div class="text-xs text-gray-400">Min</div>
            </div>
            <div class="bg-gray-50 rounded-lg p-3 text-center">
              <div class="text-lg font-bold" style="color: ${canary};">${avg}</div>
              <div class="text-xs text-gray-400">Avg</div>
            </div>
            <div class="bg-gray-50 rounded-lg p-3 text-center">
              <div class="text-lg font-bold text-gray-900">${max}</div>
              <div class="text-xs text-gray-400">Max</div>
            </div>
          </div>
        </div>
      `
    } else if (kind === 'gauge') {
      // Gauge: semicircle showing the dominant answer's percentage
      const total = data.reduce((a, b) => a + b, 0)
      const maxIdx = data.indexOf(Math.max(...data))
      const dominantPct = total > 0 ? Math.round((data[maxIdx] / total) * 100) : 0
      const dominantLabel = labels[maxIdx] || ''
      const primary = this.primaryColorValue

      new Chart(ctx, {
        type: 'doughnut',
        plugins: [ChartDataLabels],
        data: {
          labels: [dominantLabel, 'Other'],
          datasets: [{
            data: [dominantPct, 100 - dominantPct],
            backgroundColor: [primary, '#e5e7eb'],
            borderWidth: 0
          }]
        },
        options: {
          circumference: 180,
          rotation: -90,
          cutout: '70%',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
            datalabels: {
              display: false
            }
          }
        }
      })

      // Overlay the value in the centre-bottom of the gauge
      const overlay = document.createElement('div')
      overlay.style.cssText = 'position:absolute; bottom:15%; left:0; right:0; text-align:center; pointer-events:none;'
      overlay.innerHTML = `
        <div style="font-size:2.5rem; font-weight:700; color:${primary}; line-height:1;">${dominantPct}%</div>
        <div style="font-size:0.8rem; color:#6b7280; margin-top:4px;">${this.escapeHtml(dominantLabel)}</div>
      `
      this.element.parentElement.style.position = 'relative'
      this.element.parentElement.appendChild(overlay)
    } else if (kind === 'table') {
      const total = data.reduce((a, b) => a + b, 0)
      const labelStyle = this.labelStyleValue
      const pairs = labels.map((l, i) => ({ label: l, value: data[i] }))
      pairs.sort((a, b) => b.value - a.value)

      // Determine the value column based on label style
      const valueHeader = labelStyle === 'percentage' ? '% of chart values'
        : labelStyle === 'percentage_of_responses' ? '% of Measure Against'
        : labelStyle === 'both' ? 'Count / %'
        : labelStyle === 'none' ? ''
        : 'Count'

      const rows = pairs.map(p => {
        const pctOfTotal = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0
        const pctOfResponses = responsesCount > 0 ? ((p.value / responsesCount) * 100).toFixed(1) : pctOfTotal

        let displayValue
        if (labelStyle === 'none') displayValue = ''
        else if (labelStyle === 'percentage') displayValue = `${pctOfTotal}%`
        else if (labelStyle === 'percentage_of_responses') displayValue = `${pctOfResponses}%`
        else if (labelStyle === 'both') displayValue = `${p.value} (${pctOfTotal}%)`
        else displayValue = p.value

        const fillPct = total > 0 ? (p.value / total) * 100 : 0
        return `
          <tr class="border-b border-gray-100 hover:bg-gray-50">
            <td class="py-2 px-3 text-sm text-gray-900">${this.escapeHtml(p.label)}</td>
            <td class="py-2 px-3 w-1/3">
              <div class="bg-gray-100 rounded-full h-3 overflow-hidden">
                <div class="h-full rounded-full" style="width:${fillPct}%; background:${this.primaryColorValue};"></div>
              </div>
            </td>
            ${labelStyle !== 'none' ? `<td class="py-2 px-3 text-sm font-medium text-gray-700 text-right whitespace-nowrap">${displayValue}</td>` : ''}
          </tr>
        `
      }).join('')

      this.element.parentElement.innerHTML = `
        <div class="overflow-y-auto" style="max-height: 280px;">
          <table class="w-full">
            <thead class="sticky top-0 bg-white">
              <tr class="border-b border-gray-200">
                <th class="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th class="py-2 px-3 text-xs font-medium text-gray-500 uppercase w-1/3"></th>
                ${labelStyle !== 'none' ? `<th class="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">${valueHeader}</th>` : ''}
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `
    } else if (kind === 'line') {
      // Line: trend over time — labels are dates/periods, data are counts
      const primary = this.primaryColorValue
      const target = this.targetValue
      const cycleTargets = this.cycleTargetsValue
      const hasCycleTargets = Object.keys(cycleTargets).length > 0
      const datasets = [{
        data: data,
        borderColor: primary,
        backgroundColor: this.hexToRgba(primary, 0.1),
        borderWidth: 2,
        pointBackgroundColor: primary,
        pointRadius: 4,
        fill: true,
        tension: 0.3,
        clip: false,
        datalabels: {
          display: true,
          align: 'end',
          anchor: 'end',
          offset: 6,
          color: '#111827',
          backgroundColor: null,
          font: { size: 11, weight: '700' },
          formatter: (value) => value,
          clip: false
        }
      }]
      if (target > 0 || hasCycleTargets) {
        const targetData = labels.map(label => {
          const perCycle = cycleTargets[label]
          return perCycle !== undefined ? Number(perCycle) : target
        })
        datasets.push({
          data: targetData,
          borderColor: '#d1d5db',
          borderWidth: 1,
          borderDash: [4, 6],
          pointRadius: 0,
          fill: false,
          tension: 0,
          datalabels: { display: false }
        })
      }
      new Chart(ctx, {
        type: 'line',
        plugins: [ChartDataLabels],
        data: { labels: labels, datasets: datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
            datalabels: { display: false }
          },
          scales: {
            x: { ticks: { maxRotation: 45, autoSkip: true }, grid: { display: false } },
            y: { beginAtZero: true, ticks: { display: false }, grid: { display: false }, border: { display: false } }
          },
          layout: { padding: { top: 32, bottom: 8 } },
          animation: { duration: 400 }
        }
      })
    } else if (kind === 'polarArea') {
      // Polar Area: proportional breakdown — segments equal angles, size shows value
      const primary = this.primaryColorValue
      new Chart(ctx, {
        type: 'polarArea',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: effectiveColors.map(c => this.hexToRgba(c, 0.75)),
            borderColor: '#ffffff',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: this.showLegendValue },
            tooltip: { enabled: true },
            datalabels: { display: false }
          },
          scales: {
            r: { ticks: { display: false }, grid: { color: '#f3f4f6' } }
          },
          animation: { duration: 800 }
        }
      })
    } else if (kind === 'radar') {
      // Radar: multi-axis comparison — one dataset across all labels
      const primary = this.primaryColorValue
      new Chart(ctx, {
        type: 'radar',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            borderColor: primary,
            backgroundColor: this.hexToRgba(primary, 0.15),
            pointBackgroundColor: primary,
            borderWidth: 2,
            pointRadius: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
            datalabels: { display: false }
          },
          scales: {
            r: {
              ticks: { precision: 0, backdropColor: 'transparent' },
              grid: { color: '#e5e7eb' },
              pointLabels: { font: { size: 11 }, color: '#374151' }
            }
          },
          animation: { duration: 800 }
        }
      })
    } else if (kind === 'stackedBar' || kind === 'stackedColumn') {
      // Stacked Bar / Stacked Column: composition breakdown
      // Treats each label as a distinct segment stacked in a single bar
      const isHorizontal = kind === 'stackedBar'
      const total = data.reduce((a, b) => a + b, 0)
      const datasets = labels.map((label, i) => ({
        label: label,
        data: isHorizontal ? [data[i]] : [data[i]],
        backgroundColor: effectiveColors[i] || colors[i],
        borderWidth: 0
      }))

      new Chart(ctx, {
        type: 'bar',
        data: {
          labels: [''],
          datasets: datasets
        },
        options: {
          indexAxis: isHorizontal ? 'y' : 'x',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: labels.length <= 8, position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const pct = total > 0 ? Math.round((ctx.raw / total) * 100) : 0
                  return `${ctx.dataset.label}: ${ctx.raw} (${pct}%)`
                }
              }
            },
            datalabels: {
              display: (ctx) => {
                const pct = total > 0 ? (ctx.dataset.data[0] / total) * 100 : 0
                return pct >= 8
              },
              color: '#ffffff',
              font: { weight: 'bold', size: 11 },
              formatter: (value) => {
                const pct = total > 0 ? Math.round((value / total) * 100) : 0
                return `${pct}%`
              }
            }
          },
          scales: {
            x: { stacked: true, grid: { display: false }, ticks: { precision: 0 } },
            y: { stacked: true, grid: { display: false }, ticks: { precision: 0 } }
          },
          animation: { duration: 600 }
        },
        plugins: [ChartDataLabels]
      })
    } else {
      // Doughnut or pie
      const labelStyle = this.labelStyleValue
      const total = data.reduce((a, b) => a + b, 0)
      const segmentColors = this.buildHighlightSegmentColors(labels, effectiveColors)
      new Chart(ctx, {
        type: kind === 'pie' ? 'pie' : 'doughnut',
        plugins: [ChartDataLabels],
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: segmentColors,
            borderWidth: 2,
            borderColor: '#ffffff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: kind === 'pie' ? '0%' : '68%',
          plugins: {
            legend: { display: this.showLegendValue },
            tooltip: { enabled: true },
            datalabels: {
              display: labelStyle !== 'none',
              // Card #4273 round 2: hardcoded white, not the configured
              // label colour -- a pre-existing inconsistency with
              // doughnut_chart_controller.js and chart_preview_controller.js
              // (both of which read labelColorValue). Noted, not fixed here:
              // this controller has no labelColor value wired up at all, so
              // fixing it properly means adding that value and threading it
              // through from the view, which is out of scope for this
              // round's anchor/wrap fixes.
              color: '#ffffff',
              // Card #4273 round 2: the default 'center' anchor/align
              // collapses every segment's label toward the ring's midpoint,
              // the same centre-collision defect fixed in
              // doughnut_chart_controller.js and chart_preview_controller.js.
              // anchor 'end' + align 'end' pushes each label out along its
              // own slice's radial line; clamp keeps it inside the canvas.
              anchor: 'end',
              align: 'end',
              clamp: true,
              font: { weight: 'bold', size: 12 },
              formatter: (value) => {
                if (value <= 0) return ''
                const pctOfTotal = Math.round((value / total) * 100)
                const pctOfResponses = responsesCount > 0 ? Math.round((value / responsesCount) * 100) : pctOfTotal
                // Card #4273 round 2: previously hid any segment under 5%
                // entirely (`if (pctOfTotal < 5) return ''`) rather than just
                // repositioning its label -- now that labels anchor to each
                // slice's own outer edge instead of its midpoint, a small
                // segment's label has room to sit beside it instead of being
                // crushed into an unreadable sliver near the centre, so it no
                // longer needs to be suppressed.
                if (labelStyle === 'count') return value
                if (labelStyle === 'percentage') return `${pctOfTotal}%`
                if (labelStyle === 'percentage_of_responses') return `${pctOfResponses}%`
                if (labelStyle === 'both') return `${value} (${pctOfTotal}%)`
                return `${pctOfTotal}%`
              }
            }
          },
          animation: { animateRotate: true, duration: 800 }
        }
      })

      if ((kind === 'doughnut') && this.showCentreValue && this.centreValueValue) {
        const overlay = document.createElement('div')
        overlay.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);text-align:center;pointer-events:none;'
        const valueEl = document.createElement('div')
        valueEl.style.cssText = `font-size:1.75rem;font-weight:800;color:${this.primaryColorValue};line-height:1.1;`
        valueEl.textContent = this.centreValueValue
        overlay.appendChild(valueEl)
        if (this.centreLabelValue) {
          const labelEl = document.createElement('div')
          labelEl.style.cssText = 'font-size:0.65rem;color:#6b7280;margin-top:3px;max-width:80px;line-height:1.2;'
          labelEl.textContent = this.centreLabelValue
          overlay.appendChild(labelEl)
        }
        this.element.parentElement.style.position = 'relative'
        this.element.parentElement.appendChild(overlay)
      }
    }
  }

  // For doughnut/pie: highlighted segments use primary color, others go muted grey.
  // Falls back to normal palette when no highlight values set.
  // Parse the comma-separated highlight values string into an array.
  get highlightList() {
    const raw = this.highlightValuesValue
    if (!raw || raw.trim() === '') return []
    return raw.split(',').map(v => v.trim()).filter(v => v)
  }

  buildHighlightSegmentColors(labels, defaultColors) {
    const highlight = this.highlightList
    if (highlight.length === 0) return defaultColors
    return labels.map((label, i) => {
      if (highlight.includes(String(label))) return defaultColors[i]
      const hex = defaultColors[i] || '#003d68'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r},${g},${b},0.25)`
    })
  }

  // For bar/column: when custom colours are supplied (effectiveColors !== buildColors output),
  // use them directly per bar — author chose them intentionally, skip highlight muting.
  // Otherwise, highlighted bars keep primary color, others render as neutral grey.
  buildHighlightBarColors(labels, effectiveColors) {
    const highlight = this.highlightList
    // Custom colours: use per-bar colour directly, no muting
    if (this.customColorsValue.length === labels.length) {
      return labels.map((_, i) => effectiveColors[i])
    }
    if (highlight.length === 0) return effectiveColors || this.primaryColorValue
    return labels.map((label, i) => {
      if (highlight.includes(String(label))) return effectiveColors ? effectiveColors[i] : this.primaryColorValue
      const hex = (effectiveColors ? effectiveColors[i] : this.primaryColorValue) || '#003d68'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r},${g},${b},0.25)`
    })
  }

  // Returns per-segment border widths: highlighted segments get a thick border.
  buildBorderWidths(labels) {
    const highlight = this.highlightList
    if (highlight.length === 0) return 0
    return labels.map(label => highlight.includes(String(label)) ? 5 : 1)
  }

  // Returns per-segment border colors matching the existing color.
  buildBorderColors(labels, colors) {
    const highlight = this.highlightList
    if (highlight.length === 0) return '#ffffff'
    return labels.map((label, i) =>
      highlight.includes(String(label)) ? colors[i] : '#ffffff'
    )
  }

  buildColors(count) {
    const primary = this.primaryColorValue || '#003d68'
    if (count <= 2) {
      return [primary, '#fcaf17']
    }
    const blues = ['#003d68', '#0066b3', '#3399cc', '#66bbdd', '#99ddee', '#cce8f4']
    blues[0] = primary
    return Array.from({ length: count }, (_, i) => blues[i % blues.length])
  }

  generateColors(count) {
    const palette = [
      '#003d68', '#0066b3', '#3399cc', '#66bbdd', '#99ddee',
      '#e74c3c', '#f39c12', '#27ae60', '#8e44ad', '#2c3e50',
      '#1abc9c', '#d35400', '#c0392b', '#7f8c8d', '#2ecc71'
    ]
    return Array.from({ length: count }, (_, i) => palette[i % palette.length])
  }

  escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]))
  }

  // Convert a hex colour to rgba with the given alpha (0–1).
  hexToRgba(hex, alpha) {
    const h = (hex || '#003d68').replace('#', '')
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    return `rgba(${r},${g},${b},${alpha})`
  }
}
